/*
  Bello — "Build Your Own" buy box, state engine (A/B Variant B, Test 12).

  Pure logic: no DOM, no fetch, no Shopify globals. The Liquid/UI layer owns rendering
  and the cart adapter owns line items; everything here is a plain function over a plain
  state object so the pricing rules can be tested on their own.

  Money is integer cents throughout — a float total drifts off the spec's $412.55 examples.

  Catalog defaults live here so the engine is testable standalone, but the UI is expected
  to merge real Shopify data on top via `withCatalog()`: variant ids, images, and the
  prices and currency of the current market. The spec numbers below are only the fallback
  when a product does not resolve.
*/
(function (global) {
  'use strict';

  var CAPSULE_PRICE = 1295;

  /* ---- Pack tiers (step 1) --------------------------------------------------
     `capsules` = capsules covered by the flat price. `minCaps` = what Add to Cart
     requires. They only differ on Machine + Filter, which covers nothing but still
     needs at least one capsule to be a usable order.

     `filterMonths` is how long the filters in the box last: one filter per six months,
     and Discovery is the only tier that ships two. It drives both the card copy and the
     subscription's first delivery, so the two can't drift apart.

     `compareAt` / `save`, per Ammiel's Dev Updates 4: both tiers are now the spec build,
     counting the filter at its own $49.00 compare-at rather than $49.99.
       wellness  = 299 + 4×12.95              = 350.80 → save 25.80
       discovery = 299 + 49.00 + 29.00 + 8×12.95 = 480.60 → save 93.95
     That settles the split this used to carry, where Wellness quoted the catalog's
     compare-at (337.85) and Discovery the build. One formula for the whole ladder now.

     `contents` is the card's one-line spec, bold, in the order the brief writes it. It
     replaces the component list each card used to carry. The derived "Up to N drinks
     included…" line stays on Wellness and Discovery — Dev Updates 4 only drops it from
     Machine + Filter, where the brief nests that bullet under that tier alone.
  */
  var PACKS = [
    {
      id: 'machine',
      name: 'Machine + Filter',
      contents: 'Machine + 1 filter',
      showValueLine: false,
      price: 29900,
      capsules: 0,
      /* No minimum capsule: the client reversed the earlier $311.95 floor — the machine can
         be bought on its own at $299, so the "from" is $299 and Add to Cart is enabled with
         zero capsules. Updated brief, 2026-09-04. */
      minCaps: 0,
      filterMonths: 6,
      priceIsFrom: true,
      compareAt: null,
      save: null,
      tag: null
    },
    {
      id: 'wellness',
      name: 'Wellness Pack',
      contents: 'Machine + 1 filter + 4 capsules',
      showValueLine: true,
      price: 32500,
      capsules: 4,
      minCaps: 4,
      filterMonths: 6,
      priceIsFrom: false,
      compareAt: 35080,
      save: 2580,
      tag: null
    },
    {
      id: 'discovery',
      name: 'Discovery Pack',
      contents: 'Machine + 2 filters + 8 capsules + Capsule holder',
      showValueLine: true,
      price: 38665,
      capsules: 8,
      minCaps: 8,
      filterMonths: 12,
      priceIsFrom: false,
      compareAt: 48060,
      save: 9395,
      tag: 'Most Popular'
    }
  ];

  /* Upgrade ladder, low to high. Each step names the tier it upgrades into and the
     capsule count that triggers it. Discovery is terminal. */
  var UPGRADES = [
    { from: 'machine', to: 'wellness', at: 4 },
    { from: 'wellness', to: 'discovery', at: 8 }
  ];

  /* ---- Individual flavours (step 2, tab 1) ----------------------------------
     Order matters: Lemon Ginger leads the carousel, and proportional bundle fills
     break ties in this order.
     `tint` is the swatch the UI falls back to when a flavour has no product image yet.
     It lives here rather than only in the injected catalog so a card never renders blank
     if the catalog is missing or fails to parse.

     `available` is absent here on purpose: stock is Shopify's answer, not ours, and it
     arrives through the injected catalog. Absent reads as sellable, so a missing or
     unparseable catalog leaves the box working exactly as it did before. */
  var FLAVOURS = [
    { id: 'lemon-ginger', name: 'Lemon Ginger', category: 'Wellness', drinks: 15, tint: '#e8b71a' },
    { id: 'mango-turmeric', name: 'Mango Turmeric', category: 'Wellness', drinks: 15, tint: '#e8871a' },
    { id: 'wild-berries', name: 'Wild Berries', category: 'Wellness', drinks: 15, tint: '#c62828' },
    { id: 'matcha-lemon', name: 'Matcha Lemon', category: 'Wellness', drinks: 15, hasAgave: true, tint: '#4a7c1e' },
    { id: 'apple-kiwi', name: 'Apple Kiwi', category: 'Zero Cal', drinks: 35, tint: '#8bc34a' },
    { id: 'hibiscus-water', name: 'Hibiscus Water', category: 'Zero Cal', drinks: 35, tint: '#d81b60' },
    { id: 'lime-cucumber', name: 'Lime Cucumber', category: 'Zero Cal', drinks: 35, tint: '#7cb342' },
    { id: 'lemon-electrolyte', name: 'Lemon Electrolyte', category: 'Electrolyte', drinks: 15, tint: '#f2c41b' },
    { id: 'grapefruit-electrolyte', name: 'Grapefruit Electrolyte', category: 'Electrolyte', drinks: 15, tint: '#e8546b' },
    { id: 'capri-aperitivo', name: 'Capri Aperitivo', category: 'Aperitivo', drinks: 10, hasAgave: true, tint: '#e04e1b' }
  ];

  /* ---- Capsule bundles (step 2, tab 2) --------------------------------------
     Quick-fill presets, NOT separate Shopify products in this flow. `price` is card
     display only — the order total always comes from getTotal().

     `caps` is the preset's own size. Only presets that fit the pack's covered capsules
     are offered (see availableBundles); the three that don't — Zero Cal Core Collection,
     Bello Experience Collection and Sample Pack — stay defined but out of the tab. They
     could only ship 8 of their 9/16/10 capsules, so the card promised more than the order
     delivered. Deferred to a later release on the AM's call (2026-08-08); the scaling
     machinery below is kept intact for when the packs can carry them.

     Mirrors the "Capsule Bundles tab — quick-fill presets" table in the spec doc; the
     test suite asserts every row of it. */
  var BUNDLES = [
    /* `tagline` and `note` are the two lines of a preset's info modal. Presets are engine
       data, not Shopify products, so there is no metafield to read them from — they are
       approved copy off the Figma and live here. The note is deliberately per preset:
       each one claims something different (the Zero Cal pack leads with calories, the
       Electrolyte pack with caffeine), so a single templated sentence would say less than
       the design does. Both are optional; a preset without them drops the line. */
    {
      id: 'functional-flavor-pack',
      name: 'Functional Flavor Pack',
      tagline: 'Four wellness blends for every moment of your day.',
      note: 'Real ingredients throughout — no artificial sweeteners, no preservatives across all four flavours.',
      caps: 8,
      price: 8900,
      mix: { 'lemon-ginger': 2, 'mango-turmeric': 2, 'wild-berries': 2, 'matcha-lemon': 2 }
    },
    {
      id: 'electrolyte-performance',
      name: 'Electrolyte Performance',
      tagline: 'Clean recovery hydration — for training days and everything in between.',
      note: 'No added sugar · no artificial sweeteners · caffeine-free throughout',
      caps: 6,
      price: 6900,
      mix: { 'lemon-electrolyte': 3, 'grapefruit-electrolyte': 3 }
    },
    {
      id: 'morning-ritual-pack',
      name: 'Morning Ritual Pack',
      tagline: 'Two wellness blends to make mornings worth waking up for.',
      note: 'Real ingredients — no artificial sweeteners, no preservatives.',
      caps: 6,
      price: 6900,
      mix: { 'lemon-ginger': 3, 'matcha-lemon': 3 }
    },
    {
      id: 'zero-cal-essentials',
      name: 'Zero Cal Essentials',
      tagline: 'Zero calories. Zero sugar. All-day refreshment.',
      note: '0 calories · 0g sugar · caffeine-free throughout · no artificial sweeteners or preservatives.',
      caps: 6,
      price: 6900,
      mix: { 'lime-cucumber': 3, 'hibiscus-water': 3 }
    },
    {
      id: 'zero-cal-core-collection',
      name: 'Zero Cal Core Collection',
      caps: 9,
      price: 9900,
      mix: { 'apple-kiwi': 3, 'hibiscus-water': 3, 'lime-cucumber': 3 }
    },
    {
      id: 'experience-collection',
      name: 'Bello Experience Collection',
      caps: 16,
      price: 16900,
      mix: {
        'lemon-ginger': 2,
        'mango-turmeric': 2,
        'wild-berries': 2,
        'matcha-lemon': 2,
        'capri-aperitivo': 2,
        'grapefruit-electrolyte': 2,
        'lemon-electrolyte': 2,
        'apple-kiwi': 2
      }
    },
    {
      id: 'sample-pack',
      name: 'Sample Pack',
      caps: 10,
      price: 10900,
      mix: {
        'lemon-ginger': 1,
        'mango-turmeric': 1,
        'wild-berries': 1,
        'matcha-lemon': 1,
        'apple-kiwi': 1,
        'hibiscus-water': 1,
        'lime-cucumber': 1,
        'lemon-electrolyte': 1,
        'grapefruit-electrolyte': 1,
        'capri-aperitivo': 1
      }
    }
  ];

  /* ---- Subscriptions (step 3) ------------------------------------------------
     Both cards load ON and are independent of each other; neither changes the CTA,
     which only ever quotes today's charge.

     The filter's first renewal is deferred because the pack already ships one. Recharge
     models it as "100% off for 1 order, then 10%": the shopper pays $0.00 at checkout and
     the first real charge lands one cycle later.

     Prices and cadences are the live subscription products', not the spec's. The spec said
     $44.99 / day 300; the products in the store charge $44.10 (10% off a $49.00 base) and
     run at 150 and 335 days. Following the store, because it is what checkout will bill —
     the spec's numbers would have made the card disagree with the receipt. See
     docs/test-12-*.md. */
  var FILTER_SUB = {
    price: 4410,
    compareAt: 4900,
    discountPercent: 10,
    /* Ammiel, 2026-08-18, resolving the three-way split between our card (6 months), the
       product description (5 months) and what Recharge bills (335 days): everything lines
       up on the 11-month subscription's own wording, "Then $44.10 every 5 months". This is
       the one line that was promised as the change. The 335-day plan still bills every 335
       days, so the card now agrees with the approved copy and the product description while
       Recharge is brought into line on their side — see the doc. */
    intervalDays: 150,
    firstChargeDay: { discovery: 335, wellness: 150, machine: 150 },
    /* Opening words of the "next filter" line, per tier. The brief writes the two 150-day
       tiers as "Next filter delivered in 5 months" and Discovery as "Next filter in 11
       months" — one idea in two shapes. Kept word for word rather than unified on our own,
       since it is approved copy; unifying is a one-line change here if Ammiel wants it. */
    firstDeliveryLead: {
      machine: 'Next filter delivered',
      wellness: 'Next filter delivered',
      discovery: 'Next filter'
    }
  };

  /* Shopify rounds the plan discount per unit, not over the line: 10% off $12.95 is
     $11.655, billed as $11.66. Quoting 10% of the whole line instead would read $93.24/mo
     against a $93.28 charge on an 8-capsule refill. Derive the unit once and multiply, so
     the card and the receipt agree. */
  /* Dev Updates 4: the shopper picks how often the refill lands. The dropdown states the
     cadence itself ("Every 4 weeks"), which is why the brief's "Monthly refill · 10% off"
     line above the panel goes and nothing replaces it — the video calls that line
     redundant once the dropdown is there. */
  var CAPSULE_SUB_WEEKS = [4, 6, 8];

  var CAPSULE_SUB_DISCOUNT = 0.9;
  var CAPSULE_SUB_UNIT = Math.round(CAPSULE_PRICE * CAPSULE_SUB_DISCOUNT);
  /* Replaced by the market currency in withCatalog(). CAD is the standalone fallback. */
  var CURRENCY = 'CAD';

  /* ---- helpers -------------------------------------------------------------- */

  function findPack(packId) {
    for (var i = 0; i < PACKS.length; i++) {
      if (PACKS[i].id === packId) return PACKS[i];
    }
    return null;
  }

  function findFlavour(flavourId) {
    for (var i = 0; i < FLAVOURS.length; i++) {
      if (FLAVOURS[i].id === flavourId) return FLAVOURS[i];
    }
    return null;
  }

  function clone(obj) {
    var out = {};
    for (var k in obj) {
      if (Object.prototype.hasOwnProperty.call(obj, k)) out[k] = obj[k];
    }
    return out;
  }

  function formatMoney(cents) {
    var sign = cents < 0 ? '-' : '';
    var abs = Math.abs(Math.round(cents));
    return sign + '$' + Math.floor(abs / 100) + '.' + ('0' + (abs % 100)).slice(-2);
  }

  /* ---- state ---------------------------------------------------------------- */

  /* Capsules come from two independent places and are kept that way:

       caps        what the shopper stepped up on the Individual Flavors tab
       bundleMix   what the selected preset contributes, held apart from the steppers

     Both are flavourId -> count maps and absent means zero. A preset used to write
     straight into `caps`, which made its flavours appear as stepper counts on the other
     tab — the shopper could not tell what they had picked from what the preset had picked
     for them. Ammiel's brief of 2026-08-17: the steppers stay at zero and the shopper
     makes up the difference there. Keeping the two apart is what makes that possible, and
     it also means selecting a preset no longer throws away individual picks.

     Everything that prices or ships the build reads them together through `totalCaps` and
     `selectedMix`; only the steppers read `caps` alone. Both toggles start on. */
  function createState(packId) {
    var pack = findPack(packId) || findPack('discovery');
    return {
      packId: pack.id,
      caps: {},
      activeBundleId: null,
      bundleMix: {},
      isFilterSub: true,
      isCapSub: true,
      capFreqWeeks: CAPSULE_SUB_WEEKS[0]
    };
  }

  /* Resolves the pack for a target PDP. Falls back to Discovery for any surface that
     doesn't map to a tier (a landing page, a future entry point). */
  function packForProduct(handle) {
    var map = {
      'bello-home-water-machine': 'machine',
      'bello-wellness-pack': 'wellness',
      'bello-discovery-pack': 'discovery'
    };
    return map[handle] || 'discovery';
  }

  function sumMix(mix) {
    var n = 0;
    for (var id in mix) {
      if (Object.prototype.hasOwnProperty.call(mix, id)) n += mix[id];
    }
    return n;
  }

  /* What the shopper stepped up themselves. */
  function individualCaps(state) {
    return sumMix(state.caps);
  }

  /* What the selected preset contributes. */
  function bundleCaps(state) {
    return sumMix(state.bundleMix);
  }

  /* The size of the whole build — the number every price, threshold and counter is about. */
  function totalCaps(state) {
    return individualCaps(state) + bundleCaps(state);
  }

  /* Stepper counts, and only those: a preset must never show up as a number on the
     Individual Flavors tab. Use `selectedMix` for anything that ships or prices. */
  function capsOf(state, flavourId) {
    return state.caps[flavourId] || 0;
  }

  /* The two sources merged — what the order actually contains. Flavour by flavour, so a
     preset's Lemon Ginger and a stepped-up one land on a single line rather than two. */
  function selectedMix(state) {
    var out = clone(state.bundleMix);
    for (var id in state.caps) {
      if (!Object.prototype.hasOwnProperty.call(state.caps, id)) continue;
      out[id] = (out[id] || 0) + state.caps[id];
    }
    return out;
  }

  /* ---- pricing -------------------------------------------------------------- */

  /* Capsules charged on top of the flat pack price. */
  function extraCaps(state) {
    var pack = findPack(state.packId);
    return Math.max(0, totalCaps(state) - pack.capsules);
  }

  /* The single source of truth for the total: CTA and order summary both read this.
     Machine + Filter covers nothing, so every capsule is an extra. */
  function getTotal(state) {
    var pack = findPack(state.packId);
    return pack.price + extraCaps(state) * CAPSULE_PRICE;
  }

  /* ---- auto-upgrade ---------------------------------------------------------
     Evaluated at the moment of an add, with the delta folded in — never as a watcher
     over the capsule count. A watcher would re-fire on a manual pack change and bounce
     the shopper straight back up, making a manual downgrade impossible.

     Chains: adding 8 capsules at once from Machine walks machine -> wellness -> discovery
     in the same action. Upgrades only ever go up; removals never downgrade.
  */
  function resolveUpgrade(packId, currentTotal, delta) {
    var next = currentTotal + delta;
    var resolved = packId;
    for (var i = 0; i < UPGRADES.length; i++) {
      if (UPGRADES[i].from === resolved && next >= UPGRADES[i].at) resolved = UPGRADES[i].to;
    }
    return resolved;
  }

  /* ---- mutations (all return a new state) ----------------------------------- */

  /* The + button is never disabled: extras are always allowed. The − stops at 0, which
     also keeps a swap at the exact minimum possible (8/8 -> remove one -> add another). */
  function setFlavour(state, flavourId, count) {
    var current = capsOf(state, flavourId);
    return applyDelta(state, flavourId, Math.max(0, count) - current);
  }

  function applyDelta(state, flavourId, delta) {
    var current = capsOf(state, flavourId);
    /* Removing a sold-out flavour stays allowed — it's the only way out if one goes out
       of stock while the shopper is mid-build. Only adding is refused. */
    if (delta > 0 && !isSellable(findFlavour(flavourId))) return state;
    var target = Math.max(0, current + delta);
    var actual = target - current;
    if (actual === 0) return state;

    var next = clone(state);
    next.caps = clone(state.caps);
    if (target === 0) {
      delete next.caps[flavourId];
    } else {
      next.caps[flavourId] = target;
    }
    /* The preset survives a stepper. It used to be cleared here, back when both wrote to
       the same map and a manual change would otherwise have left the card lit next to a
       mix it no longer described. They are separate now, and topping a preset up is the
       flow the brief asks for. */
    if (actual > 0) next.packId = resolveUpgrade(state.packId, totalCaps(state), actual);
    return next;
  }

  /* Manual tier change. Keeps the capsule selection and recomputes everything against
     the new pack — no upgrade evaluation here, that's what makes downgrading possible.

     A preset the new tier cannot cover whole is dropped, the same rule that keeps it out
     of the tab in the first place. Downgrading with it kept would leave its capsules
     counted in the pill and charged in the total while the tab it lives on shows the
     empty state — a selection the shopper can see the price of but not the source of,
     and no way to take it off. Individual picks are untouched: those stay visible on
     their steppers at any tier. */
  function setPack(state, packId) {
    var pack = findPack(packId);
    if (!pack) return state;
    var next = clone(state);
    next.packId = packId;

    var bundle = findBundle(state.activeBundleId);
    if (bundle && !fitsPack(bundle, pack)) {
      next.activeBundleId = null;
      next.bundleMix = {};
    }
    return next;
  }

  /* ---- bundle fill ----------------------------------------------------------
     Exact fill when the preset fits the pack's covered capsules; otherwise scaled down
     proportionally to that cap using largest-remainder, so the parts always sum to the
     cap exactly (Sample Pack's 10 singles land as 8 distinct flavours, in catalog order).

     With today's data only the exact-fit branch is reachable, since availableBundles
     hides every over-cap preset. The scaling path is kept — and still tested — because
     it is what those presets need to come back.
  */
  function scaleMix(mix, from, to) {
    var ids = [];
    for (var id in mix) {
      if (Object.prototype.hasOwnProperty.call(mix, id)) ids.push(id);
    }
    // Catalog order keeps the result deterministic when remainders tie.
    ids.sort(function (a, b) {
      return flavourIndex(a) - flavourIndex(b);
    });

    var out = {};
    var assigned = 0;
    var remainders = [];
    for (var i = 0; i < ids.length; i++) {
      var exact = (mix[ids[i]] * to) / from;
      var floor = Math.floor(exact);
      out[ids[i]] = floor;
      assigned += floor;
      remainders.push({ id: ids[i], rest: exact - floor, order: i });
    }
    remainders.sort(function (a, b) {
      return b.rest - a.rest || a.order - b.order;
    });
    for (var j = 0; assigned < to && j < remainders.length; j++) {
      out[remainders[j].id] += 1;
      assigned += 1;
    }
    for (var k = 0; k < ids.length; k++) {
      if (out[ids[k]] === 0) delete out[ids[k]];
    }
    return out;
  }

  function flavourIndex(id) {
    for (var i = 0; i < FLAVOURS.length; i++) {
      if (FLAVOURS[i].id === id) return i;
    }
    return FLAVOURS.length;
  }

  function findBundle(bundleId) {
    for (var i = 0; i < BUNDLES.length; i++) {
      if (BUNDLES[i].id === bundleId) return BUNDLES[i];
    }
    return null;
  }

  /* A preset replaces whatever preset was selected before — one at a time — and leaves the
     steppers alone. The upgrade rule then runs once, with the new fill as the delta over
     the individual picks, so the tier reacts to the build as a whole.

     The mix is resolved here and stored, not recomputed on read: an over-cap preset is
     scaled to what the pack can carry, and re-deriving that later would let a manual tier
     change quietly rewrite a selection the shopper already made. */
  function applyBundle(state, bundleId) {
    var bundle = findBundle(bundleId);
    if (!bundle) return state;
    var pack = findPack(state.packId);
    var cap = pack.capsules;
    var mix = bundle.caps <= cap ? clone(bundle.mix) : scaleMix(bundle.mix, bundle.caps, cap);

    var next = clone(state);
    next.bundleMix = mix;
    next.activeBundleId = bundleId;
    next.packId = resolveUpgrade(state.packId, individualCaps(state), sumMix(mix));
    return next;
  }

  /* The other half of the Add/Remove button. Removing never downgrades the tier, the same
     one-way rule every other removal follows. */
  function clearBundle(state) {
    if (!state.activeBundleId) return state;
    var next = clone(state);
    next.activeBundleId = null;
    next.bundleMix = {};
    return next;
  }

  /* What the card's button does: the selected preset comes off, any other goes on. */
  function toggleBundle(state, bundleId) {
    return state.activeBundleId === bundleId ? clearBundle(state) : applyBundle(state, bundleId);
  }

  /* A preset is offered only when the pack covers it whole. An over-cap preset used to
     show and scale down, which meant the card advertised 16 capsules and the order shipped
     8 — capsules silently missing. One rule now covers both ends: too big and it can't be
     delivered, too small and there's nothing to offer, either way the tab shows the empty
     state pointing at Individual Flavors.

     Derived from the data, not hardcoded: with today's catalog Machine + Filter (covers 0)
     and Wellness (4) get nothing since the smallest preset is 6, and Discovery (8) gets the
     four presets at 6 and 8. Raise a pack's `capsules` or add a smaller preset and the tab
     fills in on its own. */
  function fitsPack(bundle, pack) {
    return bundle.caps <= pack.capsules;
  }

  /* Same rule, other axis: a flavour Shopify has no stock for can't be delivered either.
     Offering it costs more than hiding it — the build posts as a single atomic call, so
     one sold-out line rejects the whole order and the shopper gets a generic error after
     picking everything. Unknown reads as sellable; only an explicit false hides. */
  function isSellable(flavour) {
    return !!flavour && flavour.available !== false;
  }

  function sellableFlavours() {
    return FLAVOURS.filter(isSellable);
  }

  /* A preset is only as deliverable as its scarcest flavour. Today every offered preset
     is fully in stock, so this changes nothing on screen — it's what keeps a preset from
     quietly becoming unbuyable the day one of its flavours sells out. */
  function mixIsSellable(bundle) {
    for (var id in bundle.mix) {
      if (!Object.prototype.hasOwnProperty.call(bundle.mix, id)) continue;
      if (!isSellable(findFlavour(id))) return false;
    }
    return true;
  }

  function availableBundles(state) {
    var pack = findPack(state.packId);
    return BUNDLES.filter(function (bundle) {
      return fitsPack(bundle, pack) && mixIsSellable(bundle);
    });
  }

  /* ---- pack card copy (step 1) ----------------------------------------------
     Ammiel's brief of 2026-08-17 adds a value line under each card's component list, and
     puts the saving on a line of its own below it. Machine + Filter has no saving, so it
     gets one line; the other two get two.

     Every number is derived from the catalog rather than typed in, so the copy cannot
     drift from what the box actually sells:

       drinks     the roomiest capsule makes 35 drinks, hence "Up to". A tier quotes it
                  against the capsules the CTA requires — 1, 4 and 8 — giving the brief's
                  35 / 140 / 280.
       per drink  $12.95 over the same 35 drinks is $0.37; over the thinnest capsule's 10
                  it is $1.30. That is the brief's range, and it moves if the range of
                  flavours does.

     The whole catalog is measured, not just what is in stock today: the range describes
     what a pack can carry, and hiding a sold-out flavour should not silently reprice the
     copy on every card. */
  function drinksPerCapsule() {
    var most = 0;
    var fewest = Infinity;
    for (var i = 0; i < FLAVOURS.length; i++) {
      var n = FLAVOURS[i].drinks;
      if (!n) continue;
      if (n > most) most = n;
      if (n < fewest) fewest = n;
    }
    return { most: most, fewest: fewest };
  }

  function getPackCard(packId) {
    var pack = findPack(packId);
    if (!pack) return null;
    var perCapsule = drinksPerCapsule();
    /* The machine has no minimum capsule, but the line is aspirational where it is still
       printed, so it counts at least one capsule's worth. */
    var minForCopy = Math.max(pack.minCaps, 1);

    return {
      packId: pack.id,
      drinks: perCapsule.most * minForCopy,
      perDrinkLow: Math.round(CAPSULE_PRICE / perCapsule.most),
      perDrinkHigh: Math.round(CAPSULE_PRICE / perCapsule.fewest),
      filterMonths: pack.filterMonths,
      /* The bold line every card carries. */
      contents: pack.contents,
      /* And the drinks line beneath it, on the tiers that still show one. Machine +
         Filter returns null: Dev Updates 4 drops the line from that card only. */
      value: pack.showValueLine
        ? 'Up to ' +
          perCapsule.most * minForCopy +
          ' drinks included. ' +
          formatMoney(Math.round(CAPSULE_PRICE / perCapsule.most)) +
          '–' +
          formatMoney(Math.round(CAPSULE_PRICE / perCapsule.fewest)) +
          ' per drink, depending on your ' +
          (minForCopy === 1 ? 'choice' : 'choices') +
          '. ' +
          pack.filterMonths +
          '-month filter supply covered'
        : null,
      /* Same figure as the badge beside the pack name, read off `save` so the two can
         never quote different numbers on one card. */
      save: pack.save ? 'Save ' + formatMoney(pack.save) + ' vs. buying separately' : null
    };
  }

  /* ---- slots counter (pill + sub-label) -------------------------------------
     Returned as structured data plus an English default, so the Liquid layer can render
     the EN/FR strings from locales without re-deriving the state.

     SPEC? The source doc was truncated on the last two rows; reconstructed from the
     Figma states ("8 included · 1 extra at $12.95 each"). */
  function getSlotsCounter(state) {
    var pack = findPack(state.packId);
    var n = totalCaps(state);
    var each = formatMoney(CAPSULE_PRICE);

    /* Machine + Filter covers nothing, so there is no "included" count to divide by. The
       pill still reads n/x like every other tier — one format across the box — using the
       upgrade threshold as the denominator, which is the only number that means anything
       here: reach it and the tier becomes Wellness. */
    if (pack.capsules === 0) {
      var upgrade = UPGRADES[0];
      var nextUp = findPack(upgrade.to);
      var toUpgrade = upgrade.at - n;
      return {
        key: n === 0 ? 'none_added' : 'n_added',
        tone: n === 0 ? 'amber' : 'green',
        count: n,
        min: pack.minCaps,
        included: 0,
        extras: n,
        pill: n + '/' + upgrade.at + ' chosen' + (n >= upgrade.at ? ' ✓' : ''),
        /* Counts down as capsules go in, so the sub-label answers "what do I get for one
           more?" at every step. Past the threshold only a manual downgrade can be here,
           and the useful thing to say is what the tier above would cover. */
        sub:
          toUpgrade > 0
            ? each + ' each · add ' + toUpgrade + ' to upgrade to ' + nextUp.name
            : each + ' each · ' + nextUp.name + ' covers ' + upgrade.at
      };
    }

    if (n < pack.capsules) {
      return {
        key: 'below_min',
        tone: 'amber',
        count: n,
        min: pack.capsules,
        included: pack.capsules,
        extras: 0,
        pill: n + '/' + pack.capsules + ' chosen',
        sub: 'Your pack covers ' + pack.capsules + ' capsules · choose at least ' + pack.capsules + ' to continue'
      };
    }

    if (n === pack.capsules) {
      return {
        key: 'at_min',
        tone: 'green',
        count: n,
        min: pack.capsules,
        included: pack.capsules,
        extras: 0,
        pill: n + '/' + pack.capsules + ' chosen ✓',
        sub: 'Your pack covers ' + pack.capsules + ' capsules · add more at ' + each + ' each'
      };
    }

    var extras = n - pack.capsules;
    return {
      key: 'above_min',
      tone: 'green',
      count: n,
      min: pack.capsules,
      included: pack.capsules,
      extras: extras,
      pill: n + ' chosen ✓',
      sub: pack.capsules + ' included · ' + extras + ' extra at ' + each + ' each'
    };
  }

  /* ---- order summary --------------------------------------------------------
     Re-derived on every change. The capsules row exists to make clear the covered
     capsules are NOT an extra charge, so it quotes their retail value struck through.

     SPEC? "You save" has no formula in the logic doc; using the pack's badge saving,
     which is the only figure the shopper has already seen. Confirm before launch. */
  function getOrderSummary(state, today) {
    var pack = findPack(state.packId);
    var n = totalCaps(state);
    var extras = extraCaps(state);
    var rows = [];

    rows.push({ type: 'pack', label: pack.name, amount: pack.price });

    if (pack.capsules > 0) {
      rows.push({
        type: 'included_capsules',
        label: pack.capsules + '× Capsule (Included)',
        count: pack.capsules,
        retail: pack.capsules * CAPSULE_PRICE,
        amount: 0,
        badge: 'Included'
      });
    }

    if (extras > 0) {
      rows.push({
        type: 'extra_capsules',
        label: extras + '× Extra Capsules',
        count: extras,
        amount: extras * CAPSULE_PRICE
      });
    }

    if (pack.save) {
      rows.push({ type: 'savings', label: 'You save', amount: pack.save });
    }

    /* Dev Updates 4: the summary used to say nothing about the two subscriptions the
       shopper is also signing up for. Each row appears with its toggle and leaves with it,
       and neither moves `total` — today's charge is the pack, and the subscriptions bill
       their first cycle later. `amount: null` marks a row that states a date instead of a
       figure, so the renderer knows not to print a price beside it. */
    if (state.isFilterSub) {
      var filterSub = getFilterSubscription(state);
      rows.push({
        type: 'filter_subscription',
        label:
          'Filter subscription - first charge ' +
          formatMoney(filterSub.price) +
          ' on ' +
          formatLongDate(dateInDays(filterSub.firstChargeDay, today)),
        amount: null
      });
    }

    if (state.isCapSub) {
      var capSub = getCapsuleSubscription(state);
      rows.push({
        type: 'capsule_subscription',
        label:
          'Capsule subscription - first charge ' +
          formatMoney(capSub.monthly) +
          ' on ' +
          formatLongDate(dateInDays(capSub.firstChargeDay, today)),
        amount: null
      });
    }

    return {
      rows: rows,
      total: getTotal(state),
      currency: CURRENCY,
      selectedCapsules: n
    };
  }

  /* ---- CTA ------------------------------------------------------------------
     Always generic, always today's charge, never affected by the subscription toggles. */
  function getCtaState(state) {
    var pack = findPack(state.packId);
    var n = totalCaps(state);
    var enabled = n >= pack.minCaps;
    return {
      enabled: enabled,
      packName: pack.name,
      total: getTotal(state),
      label: 'Add to Cart — ' + pack.name + ' ' + formatMoney(getTotal(state)) + ' ' + CURRENCY
    };
  }

  /* Line directly above Add to Cart. Machine + Filter has no capsule minimum, so the
     line stays hidden and the button stays unlocked. Wellness and Discovery keep it
     visible for the whole time that pack is selected. The "n/x chosen" pill at the top
     of the selector is a separate surface and is not derived from this. */
  function getProgressLine(state) {
    var pack = findPack(state.packId);
    var n = totalCaps(state);
    if (!pack || pack.id === 'machine') {
      return { visible: false, met: false, text: '' };
    }
    if (n < pack.minCaps) {
      var remaining = pack.minCaps - n;
      return {
        visible: true,
        met: false,
        text:
          n +
          ' / ' +
          pack.minCaps +
          ' capsules chosen — choose ' +
          remaining +
          ' more to continue to cart'
      };
    }
    return {
      visible: true,
      met: true,
      text: n + ' capsules chosen — Add to Cart below to continue your purchase.'
    };
  }

  /* ---- subscription cards ---------------------------------------------------
     Independent toggles; either, both or neither may be on.

     The capsule refill never drops below the pack's minimum, so a Discovery shopper who
     picked 5 still subscribes on 8.

     The spec floors this at the pack's *covered* capsules. That reads $0.00/mo on
     Machine + Filter, which covers none — so the floor is the pack's minimum instead.
     Identical on Wellness and Discovery (4 and 8 either way); on Machine it quotes the
     one capsule the CTA already requires. Flagged in docs/test-12-*.md.

     `monthly` is the theoretical 10% here. The UI must prefer the price Shopify
     computed from the Recharge selling plan allocation when one is available — the
     plans run at 10.05% because a flat 10% of $12.95 rounds up to $11.66. */
  function getCapsuleSubscription(state) {
    var pack = findPack(state.packId);
    var capsN = Math.max(totalCaps(state), pack.minCaps, 1);
    var retail = capsN * CAPSULE_PRICE;
    var weeks = CAPSULE_SUB_WEEKS.indexOf(state.capFreqWeeks) === -1
      ? CAPSULE_SUB_WEEKS[0]
      : state.capFreqWeeks;

    return {
      active: state.isCapSub,
      capsN: capsN,
      retail: retail,
      monthly: capsN * CAPSULE_SUB_UNIT,
      discountPercent: 10,
      freqWeeks: weeks,
      freqOptions: CAPSULE_SUB_WEEKS.slice(),
      /* The refill is not shipped with the pack, so the first charge is one whole cycle
         out — the same shape as the filter's. */
      firstChargeDay: weeks * 7,
      mix: selectedMix(state)
    };
  }

  /* The card says when the next filter arrives, which is not the same date on every tier:
     Machine and Wellness ship one filter and Discovery ships two, so the second tier waits
     twice as long before it needs another. Ammiel's brief of 2026-08-17 asks the line to
     say so.

     Both numbers are read off the live subscription products rather than written down —
     150 days is 5 months, 335 is 11 — so the sentence can only ever say what Recharge is
     actually set to bill. */
  function monthsFromDays(days) {
    return Math.round(days / 30);
  }

  /* ---- dates ----------------------------------------------------------------
     The order summary names the day of the first real charge, written out — "January 17,
     2027", not 17/01/2027 — because that is what the brief asks for and it is unambiguous
     to a reader in either country the store sells to.

     `from` exists so a test can pin today; nothing in the page passes it. Months are
     spelled out here rather than taken from toLocaleDateString, which would follow the
     browser's locale and put a French month on the English page. When these strings move
     to locales/, this array is the one thing to translate. */
  var MONTH_NAMES = [
    'January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December'
  ];

  function dateInDays(days, from) {
    var d = from ? new Date(from.getTime()) : new Date();
    d.setDate(d.getDate() + Number(days || 0));
    return d;
  }

  function formatLongDate(date) {
    return MONTH_NAMES[date.getMonth()] + ' ' + date.getDate() + ', ' + date.getFullYear();
  }

  /* Ignores anything the dropdown could not have offered, so a stale value in a restored
     state cannot put a frequency on the cart that no plan backs. */
  function setCapFreq(state, weeks) {
    var n = Number(weeks);
    if (CAPSULE_SUB_WEEKS.indexOf(n) === -1) return state;
    var next = clone(state);
    next.capFreqWeeks = n;
    return next;
  }

  function getFilterSubscription(state) {
    var firstChargeDay = FILTER_SUB.firstChargeDay[state.packId];
    var firstDeliveryMonths = monthsFromDays(firstChargeDay);
    var renewalMonths = monthsFromDays(FILTER_SUB.intervalDays);

    return {
      active: state.isFilterSub,
      price: FILTER_SUB.price,
      compareAt: FILTER_SUB.compareAt,
      discountPercent: FILTER_SUB.discountPercent,
      intervalDays: FILTER_SUB.intervalDays,
      firstChargeDay: firstChargeDay,
      firstDeliveryMonths: firstDeliveryMonths,
      renewalMonths: renewalMonths,
      hint:
        FILTER_SUB.firstDeliveryLead[state.packId] +
        ' in ' +
        firstDeliveryMonths +
        ' months. Then every ' +
        renewalMonths +
        ' months'
    };
  }

  function setFilterSub(state, active) {
    var next = clone(state);
    next.isFilterSub = !!active;
    return next;
  }

  function setCapSub(state, active) {
    var next = clone(state);
    /* The toggle only means something once there are capsules to refill — it cannot be
       switched on with an empty selection (updated brief, 2026-09-04). */
    next.isCapSub = !!active && totalCaps(state) > 0;
    return next;
  }

  /* ---- catalog injection ----------------------------------------------------
     Merges Shopify-sourced data onto the catalog defaults by id: identity, and the
     prices and currency of the market the page is rendering. */
  function withCatalog(overrides) {
    if (!overrides) return;
    if (overrides.currency) CURRENCY = String(overrides.currency);
    if (overrides.capsulePrice > 0) {
      CAPSULE_PRICE = overrides.capsulePrice;
      CAPSULE_SUB_UNIT = Math.round(CAPSULE_PRICE * CAPSULE_SUB_DISCOUNT);
    }
    if (overrides.filter) {
      if (overrides.filter.price > 0) FILTER_SUB.price = overrides.filter.price;
      if (overrides.filter.compareAt > 0) FILTER_SUB.compareAt = overrides.filter.compareAt;
    }
    if (typeof api !== 'undefined') api.CAPSULE_PRICE = CAPSULE_PRICE;
    ['packs', 'flavours', 'bundles'].forEach(function (key) {
      var target = key === 'packs' ? PACKS : key === 'flavours' ? FLAVOURS : BUNDLES;
      var source = overrides[key];
      if (!source) return;
      target.forEach(function (item) {
        var patch = source[item.id];
        if (!patch) return;
        for (var k in patch) {
          if (Object.prototype.hasOwnProperty.call(patch, k)) item[k] = patch[k];
        }
      });
    });
  }

  /* ---- Product information modals (step 2) -----------------------------------
     The `i` button on every capsule and bundle card opens a read-only modal. What it
     prints is either derived here or comes from the product's own metafields, merged in
     by withCatalog() as `flavour.info`. Deriving the per-drink price here rather than in
     the markup is what keeps the modal and the pack card from ever quoting different
     numbers: both divide CAPSULE_PRICE by the same drink counts.

     Every block is optional. A metafield nobody has filled yet arrives empty and the
     modal drops that block instead of printing a heading with nothing under it. */
  var PROFILE_STEPS = 5;

  /* Metafields store taste as 0-100 ("Sweetness|20") because that is what the bars are
     drawn from; the label beside the bar is that same number on a 5-point scale. One
     source, two readings — never a hand-typed score that can drift from the bar. */
  function profileScore(value) {
    var n = Math.round(Number(value) / (100 / PROFILE_STEPS));
    if (!isFinite(n) || n < 1) return 1;
    return Math.min(n, PROFILE_STEPS);
  }

  function profileWidth(value) {
    var n = Number(value);
    if (!isFinite(n) || n < 0) return 0;
    return Math.min(n, 100);
  }

  function getFlavourInfo(flavourId) {
    var flavour = findFlavour(flavourId);
    if (!flavour) return null;
    var info = flavour.info || {};
    return {
      id: flavour.id,
      name: flavour.name,
      category: flavour.category,
      tint: flavour.tint,
      image: info.image || '',
      headline: info.headline || '',
      description: info.description || '',
      drinks: flavour.drinks,
      /* $12.95 over 15 drinks is $0.86 — the same division the pack card's
         "$0.37-$1.30 per drink" range runs over the roomiest and thinnest capsules. */
      perDrink: Math.round(CAPSULE_PRICE / flavour.drinks),
      /* The strip after those two. Its shape is not fixed — the Wellness flavours state
         what they permit, the Zero Cal ones state sugar and calories, the Electrolyte ones
         name their three minerals — so it is a list of value+label pairs the merchant fills
         in the `spec_stats` metafield, not something we could derive. */
      stats: (info.stats || []).slice(),
      /* Only Capri Aperitivo has one (`serving_ritual`); the block drops elsewhere. */
      ritual: info.ritual || '',
      profile: (info.profile || []).map(function (row) {
        return {
          label: row.label,
          value: profileWidth(row.value),
          score: profileScore(row.value),
          steps: PROFILE_STEPS
        };
      }),
      ingredients: (info.ingredients || []).slice(),
      bestFor: (info.bestFor || []).slice()
    };
  }

  /* Bundles are engine presets, not Shopify products, so their modal has no metafields to
     read: everything below is the preset's own mix run through the flavour table. */
  function getBundleInfo(bundleId) {
    var bundle = findBundle(bundleId);
    if (!bundle) return null;
    /* FLAVOURS order, not the mix's key order, so the list reads the same way the
       carousel does no matter how a preset was typed. */
    var contents = [];
    FLAVOURS.forEach(function (flavour) {
      var qty = bundle.mix[flavour.id];
      if (!qty) return;
      contents.push({
        id: flavour.id,
        name: flavour.name,
        category: flavour.category,
        tint: flavour.tint,
        image: flavour.image || '',
        qty: qty,
        drinks: flavour.drinks * qty
      });
    });
    var drinks = contents.reduce(function (sum, row) { return sum + row.drinks; }, 0);
    /* The eyebrow pill above a preset's name. Every preset offered today is built from one
       category, and the Figma prints that category — so it is derived rather than typed.
       A mixed preset has nothing true to say there and gets no pill. */
    var categories = contents
      .map(function (row) { return row.category; })
      .filter(function (value, index, all) { return all.indexOf(value) === index; });

    return {
      id: bundle.id,
      name: bundle.name,
      tagline: bundle.tagline || '',
      note: bundle.note || '',
      /* Merged in from the Customizer by withCatalog(); presets have no product to take
         a featured image from. */
      image: bundle.image || '',
      category: categories.length === 1 ? categories[0] : '',
      caps: bundle.caps,
      price: bundle.price,
      flavourCount: contents.length,
      drinks: drinks,
      contents: contents
    };
  }

  var api = {
    CAPSULE_PRICE: CAPSULE_PRICE,
    PACKS: PACKS,
    UPGRADES: UPGRADES,
    FLAVOURS: FLAVOURS,
    BUNDLES: BUNDLES,
    FILTER_SUB: FILTER_SUB,

    findPack: findPack,
    findFlavour: findFlavour,
    findBundle: findBundle,
    packForProduct: packForProduct,
    createState: createState,
    withCatalog: withCatalog,

    totalCaps: totalCaps,
    individualCaps: individualCaps,
    bundleCaps: bundleCaps,
    capsOf: capsOf,
    selectedMix: selectedMix,
    extraCaps: extraCaps,
    getTotal: getTotal,
    resolveUpgrade: resolveUpgrade,

    applyDelta: applyDelta,
    setFlavour: setFlavour,
    setPack: setPack,
    applyBundle: applyBundle,
    clearBundle: clearBundle,
    toggleBundle: toggleBundle,
    availableBundles: availableBundles,
    fitsPack: fitsPack,
    isSellable: isSellable,
    sellableFlavours: sellableFlavours,
    mixIsSellable: mixIsSellable,
    scaleMix: scaleMix,

    getPackCard: getPackCard,
    getSlotsCounter: getSlotsCounter,
    getOrderSummary: getOrderSummary,
    getCtaState: getCtaState,
    getProgressLine: getProgressLine,
    getCapsuleSubscription: getCapsuleSubscription,
    getFilterSubscription: getFilterSubscription,
    setFilterSub: setFilterSub,
    setCapSub: setCapSub,
    setCapFreq: setCapFreq,

    CAPSULE_SUB_WEEKS: CAPSULE_SUB_WEEKS,
    dateInDays: dateInDays,
    formatLongDate: formatLongDate,

    getFlavourInfo: getFlavourInfo,
    getBundleInfo: getBundleInfo,

    formatMoney: formatMoney
  };

  global.BelloBuyBoxEngine = api;
  if (typeof module !== 'undefined' && module.exports) module.exports = api;
})(typeof globalThis !== 'undefined' ? globalThis : this);
